// 拖拽层：把「合法动作」做成可以拖到目标上的手势（三国杀式的桌游操作）。
//
// 设计：合法性完全来自 Core 下发的 legal_actions；这里只是把每条 action 翻译成
// 「某个可拖元素 → 某个落点」的映射。没有拖动（点一下）时，若只有唯一落点就直接执行，
// 否则进入「持牌待放」状态，下一次点落点即执行。触摸与鼠标共用 pointer 事件。

import { useSyncExternalStore } from "react";

import { CardBack } from "./assets.tsx";
import { table } from "./store.ts";

export interface DropPayload {
  /** 没有实物牌面时才显示的对象名称。 */
  label: string;
  /** 卡面图片；真正拖动时按来源卡牌的尺寸渲染。 */
  image?: string | null | undefined;
  /** 从牌堆拖出的未知牌用对应牌堆的卡背。 */
  back?: string | undefined;
  /** 无扫描卡面的符卡使用与来源一致的符卡实体。 */
  visual?: "spell" | undefined;
  /** 落点 id → 要回发的 action。 */
  actions: Map<string, Record<string, unknown>>;
}

export interface DragState {
  payload: DropPayload;
  x: number;
  y: number;
  active: boolean;
  width: number;
  height: number;
  grabX: number;
  grabY: number;
  source: HTMLElement | null;
}

let state: DragState | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function useDrag(): DragState | null {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}

export function playerDropId(playerId: string): string {
  return `player:${playerId}`;
}

export function placeDropId(slot: number): string {
  return `place:${slot}`;
}

function dropIdAt(x: number, y: number): string | null {
  const element = document.elementFromPoint(x, y);
  return element?.closest<HTMLElement>("[data-drop]")?.dataset["drop"] ?? null;
}

function fire(action: Record<string, unknown> | undefined): void {
  if (action) table.send(action);
}

function clearDrag(): void {
  state?.source?.classList.remove("is-drag-source");
  if (!state) return;
  state = null;
  emit();
}

/** dropTap 供落点在「持牌待放」状态下被点击时调用。 */
export function dropTap(id: string): void {
  const payload = state?.payload;
  if (!payload) return;
  const action = payload.actions.get(id);
  if (!action) return;
  clearDrag();
  fire(action);
}

export function cancelDrag(): void {
  clearDrag();
}

export function startDrag(event: React.PointerEvent, payload: DropPayload): void {
  if (event.button !== 0 || payload.actions.size === 0) return;
  event.preventDefault();
  const startX = event.clientX;
  const startY = event.clientY;
  const host = event.currentTarget as HTMLElement;
  const source = host.querySelector<HTMLElement>("img.card-face, img") ?? host;
  const rect = source.getBoundingClientRect();
  const width = Math.max(1, rect.width);
  const height = Math.max(1, rect.height);
  const grabX = Math.min(width, Math.max(0, startX - rect.left));
  const grabY = Math.min(height, Math.max(0, startY - rect.top));
  let moved = false;

  const onMove = (moveEvent: PointerEvent): void => {
    if (!moved && Math.hypot(moveEvent.clientX - startX, moveEvent.clientY - startY) < 6) return;
    if (!moved) source.classList.add("is-drag-source");
    moved = true;
    state = {
      payload,
      x: moveEvent.clientX,
      y: moveEvent.clientY,
      active: true,
      width,
      height,
      grabX,
      grabY,
      source,
    };
    emit();
  };

  const onUp = (upEvent: PointerEvent): void => {
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    window.removeEventListener("pointercancel", onUp);

    if (moved) {
      const id = dropIdAt(upEvent.clientX, upEvent.clientY);
      const action = id ? payload.actions.get(id) : undefined;
      clearDrag();
      fire(action);
      return;
    }

    if (payload.actions.size === 1) {
      const only = [...payload.actions.values()][0];
      clearDrag();
      fire(only);
      return;
    }

    // 未拖动：进入/退出「持牌待放」，仍显示实体牌而不是悬浮文字窗。
    if (state?.payload === payload) {
      clearDrag();
      return;
    }
    state = {
      payload,
      x: startX,
      y: startY,
      active: false,
      width,
      height,
      grabX,
      grabY,
      source: null,
    };
    emit();
  };

  window.addEventListener("pointermove", onMove);
  window.addEventListener("pointerup", onUp);
  window.addEventListener("pointercancel", onUp);
}

/** DragGhost 按被拿起的实体牌尺寸跟随指针；非卡牌对象才回退为文字筹码。 */
export function DragGhost() {
  const drag = useDrag();
  if (!drag) return null;
  return (
    <div
      className={`drag-ghost${drag.active ? " is-active" : " is-armed"}${drag.payload.image || drag.payload.back ? " is-card" : drag.payload.visual === "spell" ? " is-spell" : " is-object"}`}
      style={{
        left: drag.x - drag.grabX,
        top: drag.y - drag.grabY,
        width: drag.width,
        height: drag.height,
      }}
      aria-label={`正在拖动${drag.payload.label}`}
    >
      {drag.payload.image ? (
        <img src={drag.payload.image} alt="" draggable={false} />
      ) : drag.payload.back ? (
        <CardBack deck={drag.payload.back} />
      ) : drag.payload.visual === "spell" ? (
        <span className="drag-spell-card">
          <img className="spell-sigil" src="/materials/spell-sigil.webp" alt="" aria-hidden="true" />
          <span><strong>{drag.payload.label}</strong><small>拖向目标发动</small></span>
        </span>
      ) : (
        <span>{drag.payload.label}</span>
      )}
    </div>
  );
}
